import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, X, Globe, User, BookOpen, ChevronDown, LogOut, UserCircle, Settings } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import logo from "@/assets/ggc_logo_optimized.webp";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const { language, setLanguage, t } = useLanguage();
  const { user, isAdmin, isViewer, isBlogEditor, isBibleSchoolEditor, isAnnouncementEditor, isSermonEditor, isMusicEditor, isMusicViewer, isMember, signOut } = useAuth();
  
  const canAccessAdmin = isAdmin || isViewer || isBlogEditor || isBibleSchoolEditor || isAnnouncementEditor || isSermonEditor || isMusicEditor;

  const scrollToSection = (id: string) => {
    if (!isHomePage) {
      window.location.href = `/#${id}`;
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMenuOpen(false);
    }
  };

  const toggleLanguage = () => {
    if (language === "en") {
      setLanguage("ru");
    } else if (language === "ru") {
      setLanguage("es");
    } else {
      setLanguage("en");
    }
  };

  const getLanguageLabel = () => {
    switch (language) {
      case "en": return "RU";
      case "ru": return "ES";
      case "es": return "EN";
      default: return "EN";
    }
  };

  const getLanguageTitle = () => {
    switch (language) {
      case "en": return "Switch to Russian";
      case "ru": return "Cambiar a Español";
      case "es": return "Switch to English";
      default: return "Switch language";
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => scrollToSection("home")}
            className="flex items-center space-x-3 group"
          >
            <img src={logo} alt="Gods Garden Church Logo" className="h-12 w-12 object-contain" />
            <h1 className="text-2xl font-bold text-primary group-hover:opacity-80 transition-opacity">
              Gods Garden Church
            </h1>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection("home")}
              className="text-foreground hover:text-primary transition-colors"
            >
              {t("nav.home")}
            </button>
            <button
              onClick={() => scrollToSection("about")}
              className="text-foreground hover:text-primary transition-colors"
            >
              {t("nav.about")}
            </button>
            <button
              onClick={() => scrollToSection("services")}
              className="text-foreground hover:text-primary transition-colors"
            >
              {t("nav.services")}
            </button>
            {/* Ministries Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-foreground hover:text-primary transition-colors">
                {t("nav.ministries")}
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-background border-border z-50">
                <DropdownMenuItem asChild>
                  <Link to="/ministries/womens-ministry" className="cursor-pointer">
                    {t("ministries.women")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/ministries/prayer-ministry" className="cursor-pointer">
                    {t("ministries.prayer")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/missions" className="cursor-pointer">
                    {t("ministries.missions")}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Resources Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-foreground hover:text-primary transition-colors">
                <BookOpen className="w-4 h-4" />
                {t("nav.resources")}
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-background border-border z-50">
                <DropdownMenuItem asChild>
                  <Link to="/announcements" className="cursor-pointer">
                    {t("nav.announcements")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/sermons" className="cursor-pointer">
                    {t("nav.sermons")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/bible" state={{ reset: true }} className="cursor-pointer">
                    {t("nav.bible")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/bible-study" className="cursor-pointer">
                    {t("nav.biblestudy")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/bible-school" className="cursor-pointer">
                    {t("nav.bibleschool")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/daily-bible-reading" className="cursor-pointer">
                    {t("nav.dailybiblereading")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/blog" className="cursor-pointer">
                    {t("nav.blog")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/special-events" className="cursor-pointer">
                    {t("nav.specialevents")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/members" className="cursor-pointer">
                    {t("nav.members")}
                  </Link>
                </DropdownMenuItem>
                {(isMember || isAdmin || isMusicEditor || isMusicViewer) && (
                  <DropdownMenuItem asChild>
                    <Link to="/music" className="cursor-pointer">
                      {t("nav.music")}
                    </Link>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/donate"
              className="text-foreground hover:text-primary transition-colors"
            >
              {t("nav.donate")}
            </Link>
            {canAccessAdmin && (
              <Link
                to="/admin"
                className="text-foreground hover:text-primary transition-colors"
              >
                {t("nav.admin")}
              </Link>
            )}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 text-foreground hover:text-primary transition-colors"
              title={getLanguageTitle()}
            >
              <Globe className="w-4 h-4" />
              {getLanguageLabel()}
            </button>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2">
                    <UserCircle className="w-4 h-4" />
                    <span className="max-w-[120px] truncate">{user.email?.split('@')[0]}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-background border-border z-50">
                  <div className="px-2 py-2 border-b border-border mb-1">
                    <p className="text-sm font-medium truncate">{user.email}</p>
                    {(isAdmin || isViewer || isBlogEditor || isBibleSchoolEditor) && (
                      <p className="text-xs text-muted-foreground">
                        {isAdmin ? "Admin" : isViewer ? "Viewer" : isBlogEditor ? "Blog Editor" : "Bible School Editor"}
                      </p>
                    )}
                  </div>
                  <DropdownMenuItem asChild>
                    <Link to="/member-portal" className="cursor-pointer flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      {t("nav.profile")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => signOut()}
                    className="cursor-pointer flex items-center gap-2 text-destructive focus:text-destructive"
                  >
                    <LogOut className="w-4 h-4" />
                    {t("nav.signout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth">
                <Button>
                  <User className="w-4 h-4 mr-2" />
                  {t("nav.signin")}
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-foreground"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-4 max-h-[calc(100vh-100px)] overflow-y-auto">
            <button
              onClick={() => scrollToSection("home")}
              className="block w-full text-left text-foreground hover:text-primary transition-colors py-2"
            >
              {t("nav.home")}
            </button>
            <button
              onClick={() => scrollToSection("about")}
              className="block w-full text-left text-foreground hover:text-primary transition-colors py-2"
            >
              {t("nav.about")}
            </button>
            <button
              onClick={() => scrollToSection("services")}
              className="block w-full text-left text-foreground hover:text-primary transition-colors py-2"
            >
              {t("nav.services")}
            </button>
            {/* Ministries Section for Mobile */}
            <div className="py-2">
              <div className="text-foreground font-medium mb-2">
                {t("nav.ministries")}
              </div>
              <div className="pl-6 space-y-2">
                <Link
                  to="/ministries/womens-ministry"
                  className="block w-full text-left text-muted-foreground hover:text-primary transition-colors py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t("ministries.women")}
                </Link>
                <Link
                  to="/ministries/prayer-ministry"
                  className="block w-full text-left text-muted-foreground hover:text-primary transition-colors py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t("ministries.prayer")}
                </Link>
                <Link
                  to="/missions"
                  className="block w-full text-left text-muted-foreground hover:text-primary transition-colors py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t("ministries.missions")}
                </Link>
              </div>
            </div>
            {/* Resources Section for Mobile */}
            <div className="py-2">
              <div className="flex items-center gap-2 text-foreground font-medium mb-2">
                <BookOpen className="w-4 h-4" />
                {t("nav.resources")}
              </div>
              <div className="pl-6 space-y-2">
                <Link
                  to="/announcements"
                  className="block w-full text-left text-muted-foreground hover:text-primary transition-colors py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t("nav.announcements")}
                </Link>
                <Link
                  to="/sermons"
                  className="block w-full text-left text-muted-foreground hover:text-primary transition-colors py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t("nav.sermons")}
                </Link>
                <Link
                  to="/bible"
                  state={{ reset: true }}
                  className="block w-full text-left text-muted-foreground hover:text-primary transition-colors py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t("nav.bible")}
                </Link>
                <Link
                  to="/bible-study"
                  className="block w-full text-left text-muted-foreground hover:text-primary transition-colors py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t("nav.biblestudy")}
                </Link>
                <Link
                  to="/bible-school"
                  className="block w-full text-left text-muted-foreground hover:text-primary transition-colors py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t("nav.bibleschool")}
                </Link>
                <Link
                  to="/daily-bible-reading"
                  className="block w-full text-left text-muted-foreground hover:text-primary transition-colors py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t("nav.dailybiblereading")}
                </Link>
                <Link
                  to="/blog"
                  className="block w-full text-left text-muted-foreground hover:text-primary transition-colors py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t("nav.blog")}
                </Link>
                <Link
                  to="/special-events"
                  className="block w-full text-left text-muted-foreground hover:text-primary transition-colors py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t("nav.specialevents")}
                </Link>
                <Link
                  to="/members"
                  className="block w-full text-left text-muted-foreground hover:text-primary transition-colors py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t("nav.members")}
                </Link>
                {(user && (isMember || isAdmin || isMusicEditor || isMusicViewer)) && (
                  <Link
                    to="/music"
                    className="block w-full text-left text-muted-foreground hover:text-primary transition-colors py-1"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t("nav.music")}
                  </Link>
                )}
              </div>
            </div>

            <Link
              to="/donate"
              className="block w-full text-left text-foreground hover:text-primary transition-colors py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("nav.donate")}
            </Link>
            {canAccessAdmin && (
              <Link
                to="/admin"
                className="block w-full text-left text-foreground hover:text-primary transition-colors py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("nav.admin")}
              </Link>
            )}
            
            {/* Language Switcher - Enhanced for mobile */}
            <div className="pt-4 border-t border-border">
              <button
                onClick={() => {
                  toggleLanguage();
                }}
                className="flex items-center gap-3 w-full text-left text-foreground hover:text-primary transition-colors py-3 px-2 hover:bg-accent/50 rounded-md"
              >
                <Globe className="w-5 h-5" />
                <span className="font-medium">
                  {language === "en" ? "Русский (RU)" : language === "ru" ? "Español (ES)" : "English (EN)"}
                </span>
              </button>
            </div>

            {/* Auth Button - Enhanced for mobile */}
            <div className="pt-2 pb-2">
              {user ? (
                <Button
                  onClick={() => {
                    signOut();
                    setIsMenuOpen(false);
                  }}
                  className="w-full py-6 text-base"
                  variant="default"
                >
                  {t("nav.signout")}
                </Button>
              ) : (
                <Link to="/auth" className="block w-full" onClick={() => setIsMenuOpen(false)}>
                  <Button className="w-full py-6 text-base" variant="default">
                    <User className="w-5 h-5 mr-2" />
                    {t("nav.signin")}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
